import { DialogComponent } from '@theme/dialog';

class CartDiscountSheet extends DialogComponent {
  static #ACTIVE_TAB_STORAGE_KEY = 'cartDiscountSheetActiveTab';

  /** @type {number | undefined} */
  #closeTimeout;

  /** @type {HTMLElement | null} */
  #cartDrawer = null;

  /** @type {number | undefined} */
  #memberVoucherCloseTimeout;

  static get observedAttributes() {
    return ['data-active-tab'];
  }

  /**
   * Syncs the active tab when the cart markup updates.
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-active-tab' || oldValue === newValue) return;

    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.#restoreActiveTab();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#cartDrawer = this.closest('cart-drawer-component');

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('dialog:open', this.#handleOpen);
    this.#cartDrawer?.addEventListener('dialog:close', this.#handleDrawerClose);

    this.#restoreActiveTab();
  }

  /**
   * Shows the discount sheet.
   */
  showDialog() {
    this.#closeMemberVoucherDetail();

    if (this.#isPopupMode()) {
      this.#openPopup();
      return;
    }

    super.showDialog();
  }

  /**
   * Closes the discount sheet.
   */
  async closeDialog() {
    if (this.#isPopupMode()) {
      this.#closePopup();
      return;
    }

    await super.closeDialog();
    this.#closeMemberVoucherDetail();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('dialog:open', this.#handleOpen);
    this.#cartDrawer?.removeEventListener('dialog:close', this.#handleDrawerClose);
    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);
    if (this.#memberVoucherCloseTimeout) clearTimeout(this.#memberVoucherCloseTimeout);
  }

  #handleOpen = () => {
    this.#activateTab(this.#activeTab());
  };

  #handleDrawerClose = () => {
    if (!this.#isPopupMode()) return;

    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);
    this.#closeMemberVoucherDetail();
    const { dialog } = this.refs;
    if (dialog?.open) {
      dialog.classList.remove('dialog-closing');
      dialog.close();
    }

    this.#storeTab(this.#defaultTab());
    this.dataset.activeTab = this.#defaultTab();
    this.#restoreActiveTab();
  };

  removeAppliedDiscounts = async () => {
    const component = this.querySelector('cart-discount-component');
    if (!(component instanceof HTMLElement) || typeof component.removeAppliedDiscounts !== 'function') return;

    await component.removeAppliedDiscounts();
  };

  #isPopupMode() {
    return this.classList.contains('cart-discount-sheet--popup');
  }

  #openPopup() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);
    dialog.classList.remove('dialog-closing');
    dialog.show();
    this.dispatchEvent(new CustomEvent('dialog:open'));
  }

  #closePopup() {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.close();
      this.#closeMemberVoucherDetail();
      this.#storeTab(this.#defaultTab());
      this.dataset.activeTab = this.#defaultTab();
      this.#restoreActiveTab();
      this.dispatchEvent(new CustomEvent('dialog:close'));
      return;
    }

    dialog.classList.add('dialog-closing');
    this.#closeTimeout = window.setTimeout(() => {
      dialog.close();
      dialog.classList.remove('dialog-closing');
      this.#closeMemberVoucherDetail();
      this.#storeTab(this.#defaultTab());
      this.dataset.activeTab = this.#defaultTab();
      this.#restoreActiveTab();
      this.dispatchEvent(new CustomEvent('dialog:close'));
    }, 220);
  }

  /**
   * Handles tab clicks.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const memberVoucherButton = target.closest('[data-member-voucher-open]');
    if (memberVoucherButton instanceof HTMLElement) {
      event.preventDefault();
      const index = Number(memberVoucherButton.dataset.memberVoucherOpen);

      if (!Number.isNaN(index)) {
        this.#activateTab('membership');
        this.#openMemberVoucherDetail(index);
      }

      return;
    }

    const memberVoucherBack = target.closest('[data-member-voucher-back]');
    if (memberVoucherBack instanceof HTMLElement) {
      event.preventDefault();
      this.#closeMemberVoucherDetail();
      return;
    }

    const memberVoucherClose = target.closest('[data-member-voucher-close]');
    if (memberVoucherClose instanceof HTMLElement) {
      event.preventDefault();
      this.#closeMemberVoucherDetail();
      return;
    }

    const tabButton = target.closest('[data-discount-tab]');
    if (!(tabButton instanceof HTMLElement)) return;

    event.preventDefault();
    const tabId = tabButton.dataset.discountTab || this.#defaultTab();
    this.#storeTab(tabId);
    this.#activateTab(tabId);
  };

  /**
   * Mirrors the selected discount code into the input.
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'cart-discount-sheet-selected-code') return;

    const discountInput = this.querySelector('#cart-discount-sheet-input');
    if (!(discountInput instanceof HTMLInputElement)) return;

    discountInput.value = target.value;
  };

  #defaultTab() {
    return this.dataset.defaultTab || 'membership';
  }

  #activeTab() {
    return  this.#storedTab() || this.dataset.activeTab || this.#defaultTab();
  }

  #storedTab() {
    return localStorage.getItem(CartDiscountSheet.#ACTIVE_TAB_STORAGE_KEY);
  }

  /**
   * Stores the active tab in local storage.
   * @param {string} tabId
   */
  #storeTab(tabId) {
    localStorage.setItem(CartDiscountSheet.#ACTIVE_TAB_STORAGE_KEY, tabId);
  }

  #restoreActiveTab() {
    this.#activateTab(this.#activeTab());
  }

  /**
   * Stores the active tab in local storage.
   * @param {string} tabId
  */
  #activateTab(tabId) {
    this.dataset.activeTab = tabId;

    if (tabId !== 'membership') {
      this.#closeMemberVoucherDetail();
    }

    for (const tab of this.querySelectorAll('[data-discount-tab]')) {
      if (!(tab instanceof HTMLElement)) continue;
      const isActive = tab.dataset.discountTab === tabId;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of this.querySelectorAll('[data-discount-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.discountPanel !== tabId;
    }
  }

  #openMemberVoucherDetail(index) {
    const content = this.querySelector('[data-cart-discount-sheet-content]');
    const detailPanel = this.querySelector('[data-member-voucher-detail-panel]');
    if (!(content instanceof HTMLElement) || !(detailPanel instanceof HTMLElement)) return;

    if (this.#memberVoucherCloseTimeout) clearTimeout(this.#memberVoucherCloseTimeout);

    content.hidden = true;
    detailPanel.hidden = false;
    this.classList.add('cart-discount-sheet--member-voucher-detail-open');
    detailPanel.offsetWidth;
    detailPanel.classList.add('cart-discount-sheet__member-voucher-panel--open');

    for (const detail of detailPanel.querySelectorAll('[data-member-voucher-detail]')) {
      if (!(detail instanceof HTMLElement)) continue;
      detail.hidden = detail.dataset.memberVoucherDetail !== String(index);
    }

    const scroller = detailPanel.querySelector('.cart-discount-sheet__member-voucher-detail-scroller');
    if (scroller instanceof HTMLElement) {
      if (typeof scroller.scrollTo === 'function') {
        scroller.scrollTo({ top: 0, left: 0 });
      } else {
        scroller.scrollTop = 0;
      }
    }
  }

  #closeMemberVoucherDetail() {
    const content = this.querySelector('[data-cart-discount-sheet-content]');
    const detailPanel = this.querySelector('[data-member-voucher-detail-panel]');
    if (!(content instanceof HTMLElement) || !(detailPanel instanceof HTMLElement)) return;

    this.classList.remove('cart-discount-sheet--member-voucher-detail-open');
    detailPanel.classList.remove('cart-discount-sheet__member-voucher-panel--open');
    content.hidden = false;

    for (const detail of detailPanel.querySelectorAll('[data-member-voucher-detail]')) {
      if (!(detail instanceof HTMLElement)) continue;
      detail.hidden = true;
    }
    if (this.#memberVoucherCloseTimeout) clearTimeout(this.#memberVoucherCloseTimeout);
    this.#memberVoucherCloseTimeout = window.setTimeout(() => {
      detailPanel.hidden = true;
    }, 220);
  }
}

if (!customElements.get('cart-discount-sheet')) {
  customElements.define('cart-discount-sheet', CartDiscountSheet);
}
